/**
 * Shared image warmer with per-URL dedupe and keep-alive retention.
 *
 * Ephemeral `new Image()` prefetches alone are not enough: once the temporary
 * Image is GC'd, browsers may drop the decoded bitmap. Remounting an `<img>`
 * on navigation then re-decodes (and can look like a fresh network load).
 *
 * We keep each warmed Image in a Map and pinned under a hidden DOM node so
 * decoded frames stay resident for the rest of the session.
 */

/** url → load promise, so a thumb is only ever fetched once per session. */
const requests = new Map<string, Promise<void>>();

/** url → live Image — holds decoded bitmaps in memory past the load promise. */
const retained = new Map<string, HTMLImageElement>();

/** Off-screen host so retained images stay attached to the document. */
let keepAliveRoot: HTMLDivElement | null = null;

const ensureKeepAliveRoot = (): HTMLDivElement | null => {
  if (typeof document === 'undefined') return null;
  if (keepAliveRoot?.isConnected) return keepAliveRoot;
  const root = document.createElement('div');
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('data-image-prefetch-keepalive', '');
  // No `contain` — paint containment can let the browser discard decoded frames.
  root.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;z-index:-1';
  document.body.appendChild(root);
  keepAliveRoot = root;
  return root;
};

/** Pin a decoded Image so the session keeps its bitmap warm. */
const retainImage = (url: string, img: HTMLImageElement): void => {
  if (retained.has(url)) return;
  retained.set(url, img);
  const root = ensureKeepAliveRoot();
  if (root && !img.isConnected) {
    root.appendChild(img);
  }
};

const load = (url: string): Promise<void> =>
  new Promise<void>((resolve) => {
    const existing = retained.get(url);
    if (existing?.complete && existing.naturalWidth > 0) {
      resolve();
      return;
    }

    const img = existing ?? new Image();
    let settled = false;

    const done = () => {
      if (settled) return;
      settled = true;
      img.onload = null;
      img.onerror = null;
      // Retain even on error so we don't hammer a missing asset every mount.
      retainImage(url, img);
      resolve();
    };

    img.onload = () => {
      // Wait through decode so callers can attach the src and paint the same frame.
      if (typeof img.decode !== 'function') {
        done();
        return;
      }
      img.decode().then(done, done);
    };
    img.onerror = done;
    if (!existing) {
      img.src = url;
    }
    if (img.complete && img.naturalWidth > 0) {
      done();
      return;
    }
    // Re-assign src if a prior incomplete retain exists (retry path).
    if (existing) img.src = url;
  });

/**
 * Warm `url` (deduped across callers) and keep the decoded Image alive.
 * Resolves once it is decoded, or on error — callers gate UI on it, never data.
 */
export const prefetchImage = (url: string): Promise<void> => {
  const existing = requests.get(url);
  if (existing) return existing;
  const request = load(url);
  requests.set(url, request);
  return request;
};

/** True when this session already has a decoded (or retained) copy of `url`. */
export const isImageCached = (url: string): boolean => {
  const held = retained.get(url);
  if (held?.complete && held.naturalWidth > 0) return true;

  // Browser HTTP/memory cache hit without our retain map (e.g. module-load warm).
  const probe = new Image();
  probe.src = url;
  if (probe.complete && probe.naturalWidth > 0) {
    retainImage(url, probe);
    return true;
  }
  return false;
};
